import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/** Carrusel horizontal con flechas (escritorio): estado de las flechas y
 *  avance de una tarjeta por clic. Lo usan los rails y «Hoy en {ciudad}». */
export function useRailScroller(contentKey: unknown) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const refresh = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    refresh();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", refresh, { passive: true });
    window.addEventListener("resize", refresh);
    return () => {
      el.removeEventListener("scroll", refresh);
      window.removeEventListener("resize", refresh);
    };
  }, [refresh, contentKey]);

  const nudge = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Una tarjeta por clic: el paso (ancho + hueco) es la distancia entre las
    // dos primeras; snap-start alinea el borde.
    const first = el.children[0] as HTMLElement | undefined;
    const second = el.children[1] as HTMLElement | undefined;
    const step = first && second ? second.offsetLeft - first.offsetLeft : (first?.offsetWidth ?? el.clientWidth);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return { scrollerRef, canLeft, canRight, nudge };
}

export function RailArrow({
  dir,
  show,
  onClick,
  className,
}: {
  dir: "left" | "right";
  show: boolean;
  onClick: () => void;
  /** Posición vertical si no debe ir al centro del bloque (p. ej. al centro del póster). */
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={dir === "left" ? t("rails.prev") : t("rails.next")}
      onClick={onClick}
      tabIndex={show ? 0 : -1}
      className={cn(
        "absolute z-10 hidden md:flex items-center justify-center -translate-y-1/2",
        className ?? "top-1/2",
        "w-11 h-11 rounded-full bg-white/90 text-rc-navy shadow-elevated backdrop-blur",
        "transition-[transform,background-color,opacity] duration-200 ease-spring hover:scale-110 hover:bg-white active:scale-95",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2",
        dir === "left" ? "left-2" : "right-2",
        show ? "opacity-100" : "opacity-0 pointer-events-none",
      )}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className={dir === "right" ? "rotate-180" : ""}
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}
